import { useRef, useState, useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { useNavigate, useSubmit, useParams, useBlocker, Outlet } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { styled } from '@mui/material/styles'
import { zodResolver } from '@hookform/resolvers/zod'

import { getEventDetails } from '../../../../store.slice'
import { useAppSelector } from '../../../../store'
import Form from '../../../root/components/Form'
import { ModifyEventSchema } from '../../../events/Events.schema'
import ListCalendar from '../../../events/view/components/ListCalendar'
import { ErrorParsingDataCalendar } from '../CreateEvent.components'

const Pre = styled('pre')(({ theme }) => ({
  padding: '1rem',
  backgroundColor: theme.palette.background.default,
  borderRadius: '5px',
  width: '100%',
  overflowX: 'auto',
}))

export default function Step5() {
  const { t } = useTranslation('events')
  const submit = useSubmit()
  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)
  const [showRawData, setShowRawData] = useState(false)

  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))
  const methods = useForm<ModifyEventSchema>({
    defaultValues: {
      event: eventDetails.event ?? {},
      subEvents: eventDetails.subEvents,
      sessions: eventDetails.sessions,
      options: eventDetails.options,
    },
    resolver: zodResolver(ModifyEventSchema),
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      const data = ModifyEventSchema.safeParse(eventDetails)

      if ('error' in data && !data.success) {
        toast.error(t("Le schéma de donnée n'est pas valide"))
        data.error.issues.forEach(issue => {
          toast.warn(`${issue.path.toString()}: ${issue.message}`, {
            autoClose: 60000,
          })
        })
        console.error(data.error)
      }
      if ('data' in data && data.success) {
        submit(data.data, {
          method: 'POST',
          encType: 'application/json',
          action: `/admin/events/${eventId}/validation`,
        })
      }
    } catch (e) {
      console.error(e)
      toast.error(t("Le schéma de donnée n'est pas valide"))
      toast.warn(t('Erreur innatendue'))
    }
  }

  useBlocker(({ nextLocation }) => {
    if (!nextLocation.pathname.includes('/admin/events/')) {
      return !window.confirm(
        `${t(
          'Êtes-vous sûr de vouloir quitter cette page ? Des modifications non enregistrées seront perdues'
        )}.`
      )
    }
    return false
  })

  const data = useMemo(() => {
    try {
      const res = ModifyEventSchema.safeParse(eventDetails)
      return res
    } catch (e) {
      console.error(e)
      return null
    }
  }, [eventDetails])

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={onSubmitForm} ref={formRef}>
          <Stack direction="column" spacing={3}>
            <h1>{t('Résumé et validation')}</h1>
            <Divider />

            {data && 'data' in data && data.success ? (
              <ListCalendar
                data={data.data}
                showHiddenFromGenericProgram
                showWithoutSession
                showHotel
              />
            ) : (
              <ErrorParsingDataCalendar data={data} />
            )}

            <Divider />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <h4>{t('Résumé Brut')}</h4>
              <Button
                onClick={() => {
                  setShowRawData(!showRawData)
                }}
              >
                {showRawData ? t('Masquer') : t('Afficher')}
              </Button>
            </Stack>
            {showRawData ? <Pre>{JSON.stringify(eventDetails, null, '\t')}</Pre> : null}
          </Stack>

          <Stack sx={{ minHeight: '3.5rem' }} />
          <Stack
            direction="row"
            padding="0 2rem 0.5rem"
            sx={{
              position: 'fixed',
              zIndex: 1,
              width: '100%',
              bottom: { xs: '7rem', md: '3rem', lg: '2.5rem' },
              left: 0,
              pointerEvents: 'none',
            }}
          >
            <Stack minWidth={{ xs: '0', md: '340px', lg: '400px' }} />
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 2, md: 3 }}
              justifyContent="space-between"
              padding="0 1rem"
              flex="2 0 auto"
            >
              <Button
                onClick={event => {
                  event.preventDefault()
                  if (window.confirm(t('Êtes-vous sûr de vouloir annuler ?')))
                    navigate('/dashboard')
                }}
                sx={{ pointerEvents: 'all' }}
              >
                {t('Annuler')}
              </Button>
              <Stack direction="row" spacing={{ xs: 1, sm: 2, md: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    navigate(`/admin/events/${eventId}/conditions`)
                  }}
                  sx={{ pointerEvents: 'all' }}
                >
                  {t('Précédent')}
                </Button>
                <Button variant="contained" type="submit" sx={{ pointerEvents: 'all' }}>
                  {t('Valider')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Form>

        <Outlet />
      </FormProvider>
    </>
  )
}
